import axios from 'axios'
import config from '@/utils/config'
import { getCookie, clearAuthCookies } from '@/utils/auth'
import { accountReasonOf, handleAccountBlocked, isAccountBlockedError, isAccountBlockedReason } from '@/utils/accountSession'
import { applyH5CredentialHeader } from '@/utils/h5Credential'

// 定义后端返回的数据类型
export interface ApiResult<T = unknown> {
  code: number
  msg: string
  data: T
}
// 业务错误：HTTP 200 但后端 code 非 200
export class BusinessError<T = unknown> extends Error {
  code: number
  data?: T
  constructor(code: number, message: string, data?: T) {
    super(message)
    this.name = 'BusinessError'
    this.code = code
    this.data = data
  }
}

// 创建 axios 实例
const instance = axios.create({
  baseURL: '/api',
  // 全局默认 30s：慢接口由调用方显式覆盖（导出等长任务传更大 timeout），
  // 避免后端慢查询导致前端无限 loading（见 Docs/舆情看板性能优化解决方案.md §3.2）
  timeout: 30000,
  withCredentials: true, // 允许携带cookie，用于JWT验证
})

// 请求拦截器 - JWT token通过HttpOnly cookie自动携带，无需手动添加
instance.interceptors.request.use(
  (config) => {
    // H5 业务请求自动透传加密凭据（来源：页面 URL 的 encrypt 参数）
    applyH5CredentialHeader(config)
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器 - 统一处理401错误（JWT token过期或无效）
instance.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // 账号停用/删除：记录具体原因，重置用户状态并返回登录页
    const blockedReason = accountReasonOf(error.response)
    if (isAccountBlockedReason(blockedReason)) {
      console.error('🔒 账号状态受限:', { url: error.config?.url, method: error.config?.method, reason: blockedReason })
      handleAccountBlocked(blockedReason as string)
      return Promise.reject(error)
    }

    if (error.response?.status === 401) {
      // 401表示后端JWT验证失败
      console.error('🔴 后端验证失败(401):', {
        url: error.config?.url,
        method: error.config?.method,
        error: error.response?.data
      })

      // 检查是否已经在登录页，避免重复跳转
      const currentPath = window.location.pathname
      if (currentPath === '/login' || currentPath === '/web/login') {
        console.log('⚠️ 已在登录页，忽略401错误')
        return Promise.reject(error)
      }

      // 检查是否正在跳转中，避免重复处理
      if (window.location.href.includes('/login')) {
        console.log('⚠️ 正在跳转登录页，忽略401错误')
        return Promise.reject(error)
      }

      // JWT token无效或过期，清除所有认证相关的cookie
      console.log('🔐 JWT验证失败，清除所有认证信息')
      clearAuthCookies()

      console.error('登录已过期，请重新登录')

      // 延迟跳转登录页
      setTimeout(() => {
        window.location.href = '/login'
      }, 1500)
    }
    return Promise.reject(error)
  }
)

// 看板元数据（V3）：来源、窗口、统计时间与陈旧标识（仅存在响应头时回调）
export interface DashboardResponseMeta {
  source: string
  windowStart?: number
  windowEnd?: number
  asOf: number | null
  computedAt: number | null
  historyAsOf: number | null
  stale: boolean
  fallbackReason: string | null
  /** A3/A4：服务端响应时刻与旧值失效时刻（毫秒；由存储时间推导，重复读取不能延长）。 */
  servedAt: number | null
  expiresAt: number | null
  /** A3：200 响应实际刷新状态（fresh/refreshing/failed/paused 等）。 */
  taskStatus: string | null
  /** 服务端建议的下次检查/观察间隔（秒）；200 与错误响应均可能携带。 */
  retryAfterSeconds: number | null
}

export type DashboardMetaCallback = (meta: DashboardResponseMeta) => void

// 简单的请求函数
export interface RequestOptions {
  baseURL?: string
  url: string
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  data?: unknown
  params?: Record<string, unknown>
  headers?: Record<string, string>
  timeout?: number
  // 当为 true 时，后端业务错误(code≠200)不记录默认错误日志，交由调用方自行处理
  suppressBusinessErrorMessage?: boolean
  // 同时抑制业务错误和 Axios/网络错误的默认日志，交由调用方自行处理
  suppressErrorMessage?: boolean
  signal?: AbortSignal
  // V3：成功后解析看板元数据响应头（可选；其他调用方默认行为不变）
  onResponseMeta?: DashboardMetaCallback
  // V3：看板队列等待 deadline（与本次 run 总预算共用时钟；其他调用方不传）
  deadlineMs?: number
}

export type RequestRuntimeOptions = Pick<RequestOptions, 'signal' | 'timeout' | 'suppressErrorMessage' | 'onResponseMeta' | 'deadlineMs'>

function readHeader(headers: unknown, name: string): string | undefined {
  if (!headers || typeof headers !== 'object') return undefined
  const value = (headers as Record<string, unknown>)[name]
  if (typeof value === 'string') return value
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0]
  return undefined
}

function toOptionalNumber(value: string | undefined): number | undefined {
  if (value === undefined || value === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

function parseDashboardMeta(response: { headers?: unknown }): DashboardResponseMeta | null {
  const source = readHeader(response.headers, 'x-dashboard-source')
  if (!source) return null
  return {
    source,
    windowStart: toOptionalNumber(readHeader(response.headers, 'x-dashboard-window-start')),
    windowEnd: toOptionalNumber(readHeader(response.headers, 'x-dashboard-window-end')),
    asOf: toOptionalNumber(readHeader(response.headers, 'x-dashboard-as-of')) ?? null,
    computedAt: toOptionalNumber(readHeader(response.headers, 'x-dashboard-computed-at')) ?? null,
    historyAsOf: toOptionalNumber(readHeader(response.headers, 'x-dashboard-history-as-of')) ?? null,
    stale: readHeader(response.headers, 'x-dashboard-stale') === 'true',
    fallbackReason: readHeader(response.headers, 'x-dashboard-fallback-reason') ?? null,
    servedAt: toOptionalNumber(readHeader(response.headers, 'x-dashboard-served-at')) ?? null,
    expiresAt: toOptionalNumber(readHeader(response.headers, 'x-dashboard-expires-at')) ?? null,
    taskStatus: readHeader(response.headers, 'x-dashboard-task-status') ?? null,
    retryAfterSeconds: toOptionalNumber(readHeader(response.headers, 'retry-after')) ?? null,
  }
}

export default async function request<T>(requestConfig: RequestOptions): Promise<T> {
  try {
    // 处理特殊的 baseURL 格式
    let finalBaseURL = requestConfig.baseURL
    if (requestConfig.baseURL === '@BaseUrl') {
      finalBaseURL = config.BASE_URL
    }

    // onResponseMeta / deadlineMs 只作为客户端选项，不进入 axios 配置
    const { onResponseMeta, ...axiosConfig } = requestConfig
    delete axiosConfig.deadlineMs
    const finalConfig = {
      ...axiosConfig,
      baseURL: finalBaseURL,
    }

    const response = await instance.request<ApiResult<T>>(finalConfig)

    if (response.status === 200 && !response.data) {
      return Promise.resolve(null as T);
    }

  const { code, msg, data: responseData } = response.data

    // 处理成功响应
    if (code === 200) {
      // V3：成功后解析元数据头再按原规则返回 data；回调与 data 由调用方在 latest request 有效时一起应用
      if (onResponseMeta) {
        const meta = parseDashboardMeta(response)
        if (meta) onResponseMeta(meta)
      }
      return responseData
    } else {
      // 处理业务错误（允许按需关闭默认日志）
      if (!requestConfig.suppressBusinessErrorMessage && !requestConfig.suppressErrorMessage) {
        console.error(msg || '请求失败')
      }
      throw new BusinessError(code, msg || '请求失败', responseData)
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      // 跳过取消请求的错误日志
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
        throw error
      }
      
      // 401错误已在响应拦截器中统一处理，这里跳过
      if (error.response?.status !== 401 && !isAccountBlockedError(error) && !requestConfig.suppressErrorMessage) {
        const errorMsg = error.response?.data?.msg || error.message || '网络请求失败'
        console.error(errorMsg)
      }
    }
    throw error
  }
}

const prefixMap = {
  BaseUrl: config.BASE_URL,
}
/**
 * 项目中的路径转换统一入口，用于处理不同环境变量下的url地址
 * @param prefix
 * @param path
 * @returns
 */
export function serverURL(prefix: keyof typeof prefixMap, path: string) {
  return prefixMap[prefix] + path
}
